/**
 * The camera: fitted so the ring hangs in from the top edge on a landscape screen and the
 * whole set still fits a portrait phone, with a ±2° parallax eased around the look-at.
 * worldToScreen / screenToWorld go through the live camera matrices, so the physics hands
 * land exactly under the pointer.
 */
import { PerspectiveCamera, Vector3 } from 'three';
import { CONFIG } from '../config';
import type { Vec2, Vec3 } from '../types';
import { v2, v3 } from '../types';
import { damp } from '../util/math';

const DEG = Math.PI / 180;

export const CAMERA = {
  lookAt: { x: 0, y: 0.62, z: 0 },
  distance: 1.9,
  fovDeg: 40,
  maxFovDeg: 64,
  /** half extent (m) the set needs across, at the hand plane: ring rx + tube radius + sway */
  halfWidthNeeded: 0.38,
  parallaxDeg: 2,
  parallaxRate: 3.5,
} as const;

export class ChimeCamera {
  readonly camera = new PerspectiveCamera(CAMERA.fovDeg, 1, 0.1, 20);
  readonly target = new Vector3(CAMERA.lookAt.x, CAMERA.lookAt.y, CAMERA.lookAt.z);
  private dist: number = CAMERA.distance;
  private wantX = 0; private wantY = 0;
  private px = 0; private py = 0;
  private cssW = 1; private cssH = 1;
  private readonly tmp = new Vector3();
  private readonly motionScale: number;

  constructor(reducedMotion: boolean) {
    this.motionScale = reducedMotion ? 0.4 : 1;
    this.place();
  }

  resize(cssW: number, cssH: number) {
    this.cssW = Math.max(1, cssW);
    this.cssH = Math.max(1, cssH);
    const aspect = this.cssW / this.cssH;
    this.camera.aspect = aspect;
    // Fit: widen the vertical fov until the ring's width fits, then, if a phone is narrower
    // still, back the camera off. Landscape screens keep the base fov and distance.
    let fov: number = CAMERA.fovDeg;
    let dist: number = CAMERA.distance;
    const halfW = dist * Math.tan((fov / 2) * DEG) * aspect;
    if (halfW < CAMERA.halfWidthNeeded) {
      fov = Math.min(CAMERA.maxFovDeg, (2 * Math.atan(CAMERA.halfWidthNeeded / (dist * aspect))) / DEG);
      const halfW2 = dist * Math.tan((fov / 2) * DEG) * aspect;
      if (halfW2 < CAMERA.halfWidthNeeded) dist = CAMERA.halfWidthNeeded / (Math.tan((fov / 2) * DEG) * aspect);
    }
    this.camera.fov = fov;
    this.dist = dist;
    this.camera.updateProjectionMatrix();
    this.place();
  }

  setParallax(nx: number, ny: number) {
    this.wantX = Math.max(-1, Math.min(1, nx));
    this.wantY = Math.max(-1, Math.min(1, ny));
  }

  update(dt: number) {
    this.px = damp(this.px, this.wantX, CAMERA.parallaxRate, dt);
    this.py = damp(this.py, this.wantY, CAMERA.parallaxRate, dt);
    this.place();
  }

  private place() {
    const yaw = this.px * CAMERA.parallaxDeg * DEG * this.motionScale;
    const pitch = -this.py * CAMERA.parallaxDeg * DEG * this.motionScale;
    const c = this.camera;
    c.position.set(
      this.target.x + Math.sin(yaw) * Math.cos(pitch) * this.dist,
      this.target.y + Math.sin(pitch) * this.dist,
      this.target.z + Math.cos(yaw) * Math.cos(pitch) * this.dist,
    );
    c.lookAt(this.target);
    c.updateMatrixWorld(true);
  }

  worldToScreen(p: Vec3, out: Vec2 = v2()): Vec2 {
    const v = this.tmp.set(p.x, p.y, p.z).project(this.camera);
    out.x = (v.x + 1) * 0.5 * this.cssW;
    out.y = (1 - v.y) * 0.5 * this.cssH;
    return out;
  }

  screenToWorld(x: number, y: number, out: Vec3 = v3()): Vec3 {
    const planeZ = CONFIG.hand.planeZ;
    const nx = (x / this.cssW) * 2 - 1;
    const ny = 1 - (y / this.cssH) * 2;
    const v = this.tmp.set(nx, ny, 0.5).unproject(this.camera);
    const o = this.camera.position;
    const dx = v.x - o.x, dy = v.y - o.y, dz = v.z - o.z;
    const t = Math.abs(dz) < 1e-6 ? 0 : (planeZ - o.z) / dz;
    out.x = o.x + dx * t;
    out.y = o.y + dy * t;
    out.z = planeZ;
    return out;
  }
}
